"use client"

import { useState } from "react"
import {
  Modal, Stack, Text, Group, Badge, NumberInput, Button, Alert,
} from "@forestsync/ui"
import { useTranslations } from "next-intl"
import { getCountryName } from "@forestsync/db"
import { ProcurementCheckoutModal, type ProcurementTrade } from "./ProcurementCheckoutModal"
import type { MatchResult } from "@/lib/matching"
import type { WizardCriteria } from "./types"

interface ProcurementBuyModalProps {
  result: MatchResult | null
  criteria: WizardCriteria
  mandateId?: string
  onClose: () => void
}

export function ProcurementBuyModal({ result, criteria, mandateId, onClose }: ProcurementBuyModalProps) {
  const t = useTranslations("procurement")
  const [volume, setVolume] = useState<number | string>("")
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [checkoutTrade, setCheckoutTrade] = useState<ProcurementTrade | null>(null)

  if (!result) return null

  const item = result.supplyItem
  const total = typeof volume === "number" ? volume * item.price : 0

  function handleConfirm() {
    const vol = typeof volume === "number" ? volume : 0
    if (vol <= 0) {
      setError(t("volumeRequired"))
      return
    }
    setError(null)
    // Pre-authorize payment before the order is placed (handled inside the checkout modal)
    setCheckoutTrade({
      projectId: item.projectId,
      creditType: item.name,
      vintage: item.vintage,
      price: item.price,
      volume: vol,
      mandateId: mandateId ? Number(mandateId) : undefined,
    })
  }

  function handleCheckoutSuccess(orderRef: string) {
    const vol = typeof volume === "number" ? volume : 0
    setSuccess(t("orderConfirmed", { volume: vol, methodology: item.methodology, ref: orderRef }))
  }

  function handleClose() {
    setVolume("")
    setError(null)
    setSuccess(null)
    setCheckoutTrade(null)
    onClose()
  }

  // Build criteria echo text
  const criteriaEcho = [
    criteria.methodology?.length ? criteria.methodology.join(", ") : null,
    criteria.geography?.length ? `in ${criteria.geography.join(", ")}` : null,
  ].filter(Boolean).join(" ")

  return (
    <>
      <Modal opened={!!result} onClose={handleClose} title={t("buyCredits")} centered>
      <Stack gap="md">
        {criteriaEcho && (
          <Text size="sm" c="dimmed">
            {t("basedOnPreferences", { criteria: criteriaEcho })}
          </Text>
        )}

        <Group justify="space-between">
          <Text fw={600}>{item.name}</Text>
          <Badge variant="light">{item.methodology}</Badge>
        </Group>

        <Group justify="space-between">
          <Text size="sm" c="dimmed">{getCountryName(item.country)}</Text>
          <Text size="sm" c="dimmed">{t("vintage")} {item.vintage}</Text>
        </Group>

        <Text size="sm">
          €{item.price.toFixed(2)} / {t("tonnes")} · {item.availableVolume.toFixed(0)}t {t("available")}
        </Text>

        <NumberInput
          label={t("volume")}
          placeholder="0"
          value={volume}
          onChange={setVolume}
          min={1}
          max={item.availableVolume}
          step={1}
          decimalScale={2}
        />

        {total > 0 && (
          <Group justify="space-between">
            <Text fw={500}>{t("total")}</Text>
            <Text fw={600}>€{total.toFixed(2)}</Text>
          </Group>
        )}

        {error && (
          <Alert color="red" onClose={() => setError(null)} withCloseButton>
            {error}
          </Alert>
        )}

        {success && (
          <Alert color="green" onClose={handleClose} withCloseButton>
            {success}
          </Alert>
        )}

        {!success && (
          <Button
            fullWidth
            color="green"
            onClick={handleConfirm}
          >
            {t("confirmTrade")}
          </Button>
        )}
      </Stack>
    </Modal>
      <ProcurementCheckoutModal
        trade={checkoutTrade}
        onClose={() => setCheckoutTrade(null)}
        onSuccess={handleCheckoutSuccess}
      />
    </>
  )
}
